import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import { phoneUpdateSerializer } from './serializer';
import { EVENTS, ENDPOINTS, PHONE_UPDATE } from './constants';

// --- PHONE UPDATE CONTROLLER ---

let phoneVerificationPollingInterval = null;
let failedPollsCount = 0;

/**
 * 1. Soumission d'un nouveau numéro (Lancement Paiement)
 */
bus.on(EVENTS.PHONE_UPDATE.SUBMIT_NEW_NUMBER, async (newPhone) => {
  const store = getStore('services');
  const userStore = getStore('user');
  const userData = userStore.state.current;

  store.state.phoneUpdate.newPhoneNumber = newPhone;
  store.state.phoneUpdate.paymentError = null;

  // Si le paiement est désactivé dans la config, sauter le paiement et aller directement à l'OTP
  if (!PHONE_UPDATE.enablePayment) {
    store.state.phoneUpdate.newPhoneNumber = newPhone;
    bus.emit(EVENTS.PHONE_UPDATE.PAYMENT_SUCCESS);
    return;
  }

  store.state.phoneUpdate.paymentStatus = 'pending';

  const appStore = getStore('appData');
  const countryCode = userData.countryCode;
  const verificationFee =
    appStore?.state?.data?.fees?.[countryCode]?.phoneVerification || 100;

  const collectData = phoneUpdateSerializer.serializeCollect(
    {
      phoneNumber: newPhone,
      operator: store.state.phoneUpdate.operator,
      countryCode: userData.countryCode,
      amount: verificationFee
    },
    userData
  );

  const requestId = collectData.requestId;
  store.state.phoneUpdate.requestId = requestId;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: ENDPOINTS.PHONE_UPDATE.INITIATE_PHONE_VERIFICATION,
    method: 'POST',
    data: collectData
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);

      if (res.error) {
        store.state.phoneUpdate.paymentStatus = 'failed';
        store.state.phoneUpdate.paymentError =
          res.error?.message || "Erreur d'initialisation";
        return;
      }

      const verificationId =
        res.data?.id ||
        res.data?.data?.id ||
        res.data?.reference ||
        collectData.id;

      if (verificationId) {
        startPhoneVerificationPolling(verificationId);
      } else {
        store.state.phoneUpdate.paymentStatus = 'failed';
        store.state.phoneUpdate.paymentError =
          'Impossible de suivre le paiement (ID manquant)';
      }
    }
  };
  bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
});

function startPhoneVerificationPolling(verificationId) {
  const store = getStore('services');
  failedPollsCount = 0;

  if (phoneVerificationPollingInterval)
    clearInterval(phoneVerificationPollingInterval);

  phoneVerificationPollingInterval = setInterval(async () => {
    const requestId = `phone-poll-${Date.now()}`;
    const url = ENDPOINTS.PHONE_UPDATE.CHECK_PHONE_VERIFICATION_STATUS.replace(
      '{id}',
      verificationId
    );

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: url,
      method: 'GET'
    });

    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);

        const { success, status } =
          phoneUpdateSerializer.deserializeStatus(res);

        if (success) {
          clearInterval(phoneVerificationPollingInterval);
          store.state.phoneUpdate.paymentStatus = 'success';
          bus.emit(EVENTS.PHONE_UPDATE.PAYMENT_SUCCESS);
        } else if (
          status === 'failed' ||
          status === 'error' ||
          status === 'rejected'
        ) {
          clearInterval(phoneVerificationPollingInterval);
          store.state.phoneUpdate.paymentStatus = 'failed';
          store.state.phoneUpdate.paymentError =
            'Le paiement a été rejeté ou a échoué.';
        } else {
          failedPollsCount++;
          if (failedPollsCount >= 40) {
            // ~6-7 mins
            clearInterval(phoneVerificationPollingInterval);
            store.state.phoneUpdate.paymentStatus = 'failed';
            store.state.phoneUpdate.paymentError = "Délai d'attente dépassé.";
          }
        }
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  }, 10000);
}

// Stop polling if dialog is closed
bus.on(constants.EVENTS.SERVICES.CLOSE_OPERATION_INITIATION, () => {
  if (phoneVerificationPollingInterval) {
    clearInterval(phoneVerificationPollingInterval);
    phoneVerificationPollingInterval = null;
  }
});

/**
 * 2. Sauvegarde simple (Sans paiement) -> Envoi OTP direct
 */
bus.on(EVENTS.PHONE_UPDATE.SIMPLE_SAVE, () => {
  finalizePhoneUpdate();
});

/**
 * 3. Une fois le paiement réussi (ou simple save), envoyer l'OTP
 */
bus.on(EVENTS.PHONE_UPDATE.PAYMENT_SUCCESS, () => {
  const store = getStore('services');
  const userStore = getStore('user');
  const userData = userStore.state.current;
  const newPhone = store.state.phoneUpdate.newPhoneNumber;

  const requestId = `phone-otp-${Date.now()}`;
  const normalizedPhone = phoneUpdateSerializer.normalize(
    newPhone,
    userData.countryCode
  );

  bus.emit(constants.EVENTS.OTP.SEND_OTP, {
    requestId,
    channel: 'sms',
    phoneNumber: normalizedPhone,
    showContactInput: false,
    validate: true,
    email: userData.email,
    name: userData.firstName + ' ' + userData.lastName
  });

  const otpHandler = (data) => {
    if (data.requestId === requestId) {
      bus.off(constants.EVENTS.OTP.OTP_VALIDATED, otpHandler);
      finalizePhoneUpdate();
    }
  };
  bus.on(constants.EVENTS.OTP.OTP_VALIDATED, otpHandler);
});

async function finalizePhoneUpdate() {
  const store = getStore('services');
  const userStore = getStore('user');
  const userData = userStore.state.current;
  const newPhone = store.state.phoneUpdate.newPhoneNumber;
  const operator = store.state.phoneUpdate.operator;

  const validatedPhones = store.state.phoneUpdate.validatedPhones || [];
  const normalizedPhone = phoneUpdateSerializer.normalize(
    newPhone,
    userData.countryCode
  );

  // Mettre à jour la liste locale des téléphones validés
  // On ajoute uniquement si le numéro exact n'existe pas encore
  // (on NE remplace PAS par opérateur — plusieurs numéros du même réseau sont permis)
  const updatedPhones = [...validatedPhones];
  const country = userData.countryCode || 'BJ';
  const index = updatedPhones.findIndex((p) => p.phone === normalizedPhone);
  if (index === -1) {
    updatedPhones.push({ country, operator, phone: normalizedPhone, carrier: operator });
  }

  const requestId = `phone-save-${Date.now()}`;
  const savePayload = phoneUpdateSerializer.serializeSave(updatedPhones);

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${ENDPOINTS.PHONE_UPDATE.SAVE}&userId=${encodeURIComponent(userData.id)}`,
    method: 'POST',
    data: savePayload
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);

      // Mettre à jour les métadonnées de l'utilisateur pour persistence
      const updatedMetadata = {
        ...(userData.metadata || {}),
        transactionPhoneNumber: newPhone
      };
      bus.emit(constants.EVENTS.USER.UPDATE, {
        id: userData.id,
        metadata: updatedMetadata
      });

      // Signaler la fin
      store.state.transactionInitiation.phoneNumber = newPhone;
      bus.emit(EVENTS.PHONE_UPDATE.COMPLETED, newPhone);

      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Numéro validé',
        body: `Le numéro ${newPhone} est maintenant prêt.`
      });
    }
  };
  bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
}

/**
 * Reprise automatique de la transaction après validation
 */
bus.on(EVENTS.PHONE_UPDATE.COMPLETED, () => {
  bus.emit(constants.EVENTS.SERVICES.INITIATION.SUBMIT_FORM);
});

/**
 * Vérification asynchrone du statut du numéro
 */
export async function getValidPhone(currentPhone) {
  const userStore = getStore('user');
  const userData = userStore.state.current;
  const fullPhone = phoneUpdateSerializer.normalize(
    currentPhone,
    userData?.countryCode
  );
  if (!userData?.id) return { isValidated: false, existingPhones: [] };

  const requestId = `phone-check-${Date.now()}`;
  const url = `${ENDPOINTS.PHONE_UPDATE.CHECK_VALIDATED}&userId=${encodeURIComponent(userData.id)}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: url,
    method: 'GET'
  });

  return new Promise((resolve) => {
    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        const existingPhones = phoneUpdateSerializer.deserializeCheck(res);
        const isValidated = existingPhones.some((p) => p.phone === fullPhone);

        // Mettre à jour le store pour la réactivité
        const store = getStore('services');
        store.state.phoneUpdate.validatedPhones = existingPhones;

        resolve({
          success: true,
          isValidated,
          existingPhones
        });
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);

    // Safety timeout
    setTimeout(() => {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
      resolve({ isValidated: false, existingPhones: [] });
    }, 10000);
  });
}
